import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import ProtectedRoutes from "./ProtectedRoutes"
import Login from "../page/Login/Login"
import StudentLogin from "../page/StudentLogin/StudentLogin"
import ThirdPartyLogin from "../page/ThirdPartyLogin/ThirdPartyLogin"
import StudentRoutes from "./StudentRoutes"
import { useEffect, useState } from "react"
import Nav from "../component/nav/nav"
import { getProfile } from "../api/profile"
import InputEmail from "src/page/ForgotPassword/InputEmail"
import OptPassword from "src/page/ForgotPassword/OptPassword"
import SetPassword from "src/page/ForgotPassword/ResetPassword"
import useLearnerAuth from "../auth/learner/useLearnerAuth"
import LearnerAuthBootScreen from "../auth/learner/LearnerAuthBootScreen"
import RequireLearnerAuth from "../auth/learner/RequireLearnerAuth"
import RequireLearnerGuest from "../auth/learner/RequireLearnerGuest"
import { LEARNER_DEFAULT_PATH, LEARNER_LOGIN_PATH } from "../auth/learner/constants"

const THIRD_PARTY_LOGIN_PATH = "/thirdparty/login"

function TeacherPublicApp({
  isLoggedIn,
  user,
}: {
  isLoggedIn: boolean
  user: any
}) {
  return (
    <>
      <Nav isLoggedIn={isLoggedIn} user={user} />
      {isLoggedIn ? (
        <ProtectedRoutes user={user} />
      ) : (
        <Routes>
          <Route Component={Login} path="/login" />
          <Route Component={InputEmail} path="/reset-password" />
          <Route Component={OptPassword} path="/otp-password" />
          <Route Component={SetPassword} path="/set-password" />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </>
  )
}

function MainRoutes() {
  const { isBooting, isAuthenticated: isLearnerAuthenticated } = useLearnerAuth()
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("access_token")
  )
  const [user, setUser] = useState<any>(null)

  useEffect(() => {
    const access_token = localStorage.getItem("access_token")
    setIsLoggedIn(!!access_token)
  }, [isLearnerAuthenticated])

  useEffect(() => {
    const { location } = window
    const publicPaths = [
      "/login",
      LEARNER_LOGIN_PATH,
      THIRD_PARTY_LOGIN_PATH,
      "/reset-password",
      "/otp-password",
      "/set-password",
    ]

    if (isLearnerAuthenticated || isBooting) return

    if (
      !publicPaths.some((route) => location.pathname.startsWith(route)) &&
      !isLoggedIn
    ) {
      if (location.pathname.startsWith("/learner")) {
        const next = encodeURIComponent(`${location.pathname}${location.search}`)
        window.location.assign(`${LEARNER_LOGIN_PATH}?next=${next}`)
      } else {
        location.pathname = "/login"
      }
      return
    }

    if (location.pathname === "/login" && isLoggedIn) {
      location.pathname = "/"
    }

    if (isLoggedIn) {
      getProfile()
        .then((res) => {
          if (res.data && res.data.success) {
            setUser(res.data.data)
          }
        })
        .catch((error) => {
          console.log({ error })
        })
    }
  }, [isLoggedIn, isLearnerAuthenticated, isBooting])

  if (isBooting) {
    return <LearnerAuthBootScreen />
  }

  if (isLearnerAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path={LEARNER_LOGIN_PATH}
            element={<Navigate to={LEARNER_DEFAULT_PATH} replace />}
          />
          <Route
            path={THIRD_PARTY_LOGIN_PATH}
            element={<Navigate to={LEARNER_DEFAULT_PATH} replace />}
          />
          <Route
            path="/learner/*"
            element={
              <RequireLearnerAuth>
                <StudentRoutes />
              </RequireLearnerAuth>
            }
          />
          <Route path="*" element={<Navigate to={LEARNER_DEFAULT_PATH} replace />} />
        </Routes>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path={THIRD_PARTY_LOGIN_PATH} element={<ThirdPartyLogin />} />
        <Route
          path={LEARNER_LOGIN_PATH}
          element={
            <RequireLearnerGuest>
              <StudentLogin />
            </RequireLearnerGuest>
          }
        />
        {/* Logged-out learner URLs must never fall through to teacher /login */}
        <Route
          path="/learner/*"
          element={<Navigate to={LEARNER_LOGIN_PATH} replace />}
        />
        <Route
          path="/*"
          element={<TeacherPublicApp isLoggedIn={isLoggedIn} user={user} />}
        />
      </Routes>
    </BrowserRouter>
  )
}

export default MainRoutes
